import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import { ShopifyProductFilter } from "../../../../models/app/AppModels";
import {
  useGetNotSyncedShopifyProductsMutation,
  useMatchShopifyProductsWithDMSMutation,
} from "../../../../redux/reducer/features/Integration/DmsSetupSlice";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import SearchIcon from "@mui/icons-material/Search";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import { getStoreProductStatusChip } from "../../../../services/utilities/utility";
import CloseIcon from "@mui/icons-material/Close";
import { LinkInventoryDto } from "../../../../models/features/Integration/shopify";

interface ShopifyProductGridProps {
  handleNext: any;
  setSelectedProducts: (products: any[]) => void;
}

const ShopifyProductGrid: React.FunctionComponent<ShopifyProductGridProps> = (
  props,
) => {
  const [shopifyProducts, setShopifyProducts] = React.useState<
    LinkInventoryDto[]
  >([]);
  const [ShopifyProductsCount, setShopifyProductsCount] =
    React.useState<number>(0);
  const [searchShopifyProduct, setSearchShopifyProduct] = React.useState("");
  const [selectedProducts, setSelectedProducts] = React.useState<
    LinkInventoryDto[]
  >([]);
  const [shopifyProductStatus, setShopifyProductStatus] = React.useState("all");
  const [shopifyProductsMutation, shopifyProductsMutationResult] =
    useGetNotSyncedShopifyProductsMutation();
  const [matchShopifyProductsMutation, matchShopifyProductsMutationResult] =
    useMatchShopifyProductsWithDMSMutation();
  const [pageInfo, setPageInfo] = React.useState<{
    endCursor?: string;
    hasNextPage: string;
    hasPreviousPage: string;
    startCursor?: string;
    pageIndex: number;
    shopifyProductStatus?: string;
  }>({ pageIndex: 0, hasNextPage: "False", hasPreviousPage: "False" });

  const [shopifyPaging, setShopifyPaging] =
    React.useState<ShopifyProductFilter>({
      pageIndex: 0,
      pageSize: 50,
      totalRecords: 0,
      quickFilterValues: [],
      items: [],
    });

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setShopifyPaging({
      ...shopifyPaging,
      pageIndex: newPage,
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setShopifyPaging({
      ...shopifyPaging,
      pageSize: parseInt(event.target.value, 10),
      pageIndex: 0,
    });
  };

  React.useEffect(() => {
    var requestPaging = { ...shopifyPaging };
    if (shopifyPaging.pageIndex > pageInfo?.pageIndex) {
      requestPaging = {
        ...shopifyPaging,
        endCursor: pageInfo.endCursor,
        startCursor: undefined,
      };
    } else if (
      shopifyPaging.pageIndex < pageInfo?.pageIndex &&
      shopifyPaging.pageIndex !== 0
    ) {
      requestPaging = {
        ...shopifyPaging,
        endCursor: undefined,
        startCursor: pageInfo.startCursor,
      };
    } else {
      requestPaging = {
        ...shopifyPaging,
        endCursor: undefined,
        startCursor: undefined,
      };
    }
    fetchShopifyProducts(requestPaging);
  }, [shopifyPaging, shopifyProductStatus]);
  const fetchShopifyProducts = (shopifyPagingData: any) => {
    shopifyProductsMutation({
      paging: {
        ...shopifyPagingData,
        shopifyProductStatus:
          shopifyProductStatus === "all" ? undefined : shopifyProductStatus,
      },
    });
  };

  React.useEffect(() => {
    if (shopifyProductsMutationResult.data) {
      let products = shopifyProductsMutationResult.data.entity.variants.map(
        (item: any) => ({ ...item, open: false }),
      );
      setShopifyProducts(products);
      setShopifyProductsCount(shopifyProductsMutationResult.data.totalRecords);
      setPageInfo({
        ...shopifyProductsMutationResult.data.entity.pageInfo,
        pageIndex: shopifyPaging.pageIndex,
      });
    }
  }, [shopifyProductsMutationResult]);

  const debounce = (func: Function, delay: number) => {
    let timer: ReturnType<typeof setTimeout>;
    return function (this: any, ...args: any[]) {
      clearTimeout(timer);
      timer = setTimeout(() => func.apply(this, args), delay);
    };
  };

  const handleSearchTextChange = React.useCallback(
    debounce((value: any) => {
      setShopifyPaging({
        ...shopifyPaging,
        quickFilterValues: value ? [value?.trim()] : [],
        pageIndex: 0,
      });
    }, 1000),
    [],
  );
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearchShopifyProduct(value);
    handleSearchTextChange(value);
  };
  const handleNextStep = () => {
    matchShopifyProductsMutation({ request: selectedProducts }).then(
      (res: any) => {
        if (res && res.data) {
          if (res.data.status === 200) {
            props.setSelectedProducts(res.data.entityList);
            props.handleNext();
          }
        }
      },
    );
  };

  const handleSelectProduct = (productId: any) => {
    setSelectedProducts((prev) => {
      if (prev.find((product) => product.variantId === productId)) {
        return prev.filter((product) => product.variantId !== productId);
      } else {
        const selectedProduct = shopifyProducts.find(
          (product) => product.variantId === productId,
        );
        return selectedProduct ? [...prev, selectedProduct] : prev;
      }
    });
  };

  const handleSelectAll = () => {
    const currentPageProductIds = shopifyProducts.map((p) => p.variantId);
    const currentPageSelectedCount = selectedProducts.filter((p) =>
      currentPageProductIds.includes(p.variantId),
    ).length;

    if (currentPageSelectedCount === shopifyProducts.length) {
      // Deselect all current page products
      setSelectedProducts((prev) =>
        prev.filter((p) => !currentPageProductIds.includes(p.variantId)),
      );
    } else {
      // Select all current page products (keeping previously selected from other pages)
      const currentPageProducts = shopifyProducts.filter(
        (p) =>
          !selectedProducts.some(
            (selected) => selected.variantId === p.variantId,
          ),
      );
      setSelectedProducts((prev) => [...prev, ...currentPageProducts]);
    }
  };
  const handleSelectChange = (event: any) => {
    setShopifyProductStatus(event.target.value);
  };
  // Computed values for current page selection state
  const currentPageProductIds = shopifyProducts.map((p) => p.variantId);
  const currentPageSelectedCount = selectedProducts.filter((p) =>
    currentPageProductIds.includes(p.variantId),
  ).length;
  const isCurrentPageAllSelected =
    currentPageSelectedCount === shopifyProducts.length &&
    shopifyProducts.length > 0;
  const isCurrentPageIndeterminate =
    currentPageSelectedCount > 0 &&
    currentPageSelectedCount < shopifyProducts.length;

  function Row({ row, key }: { row: LinkInventoryDto; key: any }) {
    return (
      <React.Fragment>
        <TableRow key={key}>
          <TableCell sx={{ padding: "7px" }}>
            <Checkbox
              checked={selectedProducts.some(
                (product) => product.variantId === row.variantId,
              )}
              onChange={() => handleSelectProduct(row.variantId)}
              onClick={(e) => e.stopPropagation()}
            />
          </TableCell>
          <TableCell sx={{ padding: "10px" }}>{row.vendor}</TableCell>
          <TableCell sx={{ padding: "10px" }}>{row.productTitle}</TableCell>
          <TableCell sx={{ padding: "10px" }}>{row.title}</TableCell>
          <TableCell sx={{ padding: "10px" }}>{row.sku}</TableCell>
          <TableCell align="center" sx={{ padding: "10px" }}>
            {row?.price?.toFixed(2) ?? 0.0}
          </TableCell>
          <TableCell align="center" sx={{ padding: "10px" }}>
            {getStoreProductStatusChip(row.status)}
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  }
  return (
    <>
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        gap={2}
        p={0.5}
      >
        <Alert severity="info">
          Select Shopify products to link with DMS products. TargetWeb will
          automatically try to find the best matches.
        </Alert>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            alignItems: "center",
          }}
        >
          <Button
            disabled={
              selectedProducts.length === 0 ||
              matchShopifyProductsMutationResult.isLoading
            }
            size="small"
            variant="contained"
            onClick={handleNextStep}
            endIcon={
              matchShopifyProductsMutationResult.isLoading ? (
                <CircularProgress color="inherit" size={16} />
              ) : null
            }
          >
            Next
          </Button>
        </Box>
      </Box>
      <Card sx={{ borderColor: "rgba(0, 0, 0, 0.12)", border: "1px outset" }}>
        <Box
          display={"flex"}
          justifyContent={"flex-end"}
          gap={2}
          alignItems={"center"}
        >
          <Select
            value={shopifyProductStatus}
            sx={{ width: "130px", mt: 0.5 }}
            onChange={handleSelectChange}
            variant="outlined"
            size="small"
          >
            <MenuItem selected value="all">
              All
            </MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="archived">Archived</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
          </Select>
          <TextField
            id="search"
            name="search"
            placeholder="Search.."
            variant="standard"
            size="small"
            onChange={handleChange}
            value={searchShopifyProduct}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="primary" sx={{ fontSize: "20px" }} />
                </InputAdornment>
              ),
              endAdornment: searchShopifyProduct && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() =>
                      handleChange({
                        target: { value: "" },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                    edge="end"
                  >
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <TableContainer component={Paper} sx={{ height: "59vh" }}>
          <Table
            aria-label="collapsible table"
            sx={{ borderCollapse: "separate" }}
          >
            <TableHead
              sx={{
                position: "sticky",
                zIndex: 100,
                top: 0,
                background: "white",
              }}
            >
              <TableRow sx={{ height: "2vh" }}>
                <TableCell sx={{ p: "7px", width: "1%" }}>
                  <Checkbox
                    checked={isCurrentPageAllSelected}
                    onChange={handleSelectAll}
                    indeterminate={isCurrentPageIndeterminate}
                  />
                </TableCell>
                <TableCell sx={{ padding: 0, width: "7%" }}>
                  <span style={{ color: "#E0E0E0" }}>|</span> Manufacturer
                </TableCell>
                <TableCell sx={{ padding: 0 }}>
                  <span style={{ color: "#E0E0E0" }}>|</span> Product Title
                </TableCell>
                <TableCell sx={{ padding: 0 }}>
                  <span style={{ color: "#E0E0E0" }}>|</span> Variant Title
                </TableCell>
                <TableCell sx={{ padding: 0 }}>
                  <span style={{ color: "#E0E0E0" }}>|</span> SKU
                </TableCell>
                <TableCell align="center" sx={{ padding: 0, width: "7%" }}>
                  <span style={{ color: "#E0E0E0" }}>|</span> Price ($)
                </TableCell>
                <TableCell align="center" sx={{ padding: 0 }}>
                  <span style={{ color: "#E0E0E0" }}>|</span> Status
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shopifyProductsMutationResult.isLoading ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box
                      justifyContent="center"
                      alignItems="center"
                      display="flex"
                      sx={{ height: "60vh" }}
                    >
                      <CircularProgress size="2.2rem" color="inherit" />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : shopifyProducts && shopifyProducts.length > 0 ? (
                shopifyProducts.map((row: LinkInventoryDto) => (
                  <Row key={row.variantId} row={row} />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box
                      justifyContent="center"
                      alignItems="center"
                      display="flex"
                      sx={{ height: "60vh" }}
                    >
                      <Typography>No Rows to Show</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingLeft: 2,
            paddingRight: 1,
            borderTop: "1px solid rgba(224, 224, 224, 1)",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {/* Selected rows count - Left */}
          <Box sx={{ display: "flex", alignItems: "center", flex: "0 0 auto" }}>
            {selectedProducts.length > 0 && (
              <Typography variant="body2" sx={{ color: "rgba(0, 0, 0, 0.87)" }}>
                {selectedProducts.length}{" "}
                {selectedProducts.length === 1 ? "row" : "rows"} selected
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", flex: "0 0 auto" }}>
            <TablePagination
              rowsPerPageOptions={[50, 100, 250]}
              count={ShopifyProductsCount}
              rowsPerPage={shopifyPaging.pageSize}
              page={shopifyPaging.pageIndex}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelDisplayedRows={({ from, to, count }) => {
                // Custom conditions
                if (
                  searchShopifyProduct?.trim() === "" &&
                  shopifyProductStatus === "all"
                ) {
                  // Normal state, no search filter
                  return `${from}–${to} of ${count}`;
                } else {
                  let previousCount =
                    shopifyPaging.pageIndex * shopifyPaging.pageSize;
                  let currentCount = shopifyProducts?.length || 0;
                  let cumulativeCount = previousCount + currentCount;
                  let displayCount =
                    pageInfo.hasNextPage == "True"
                      ? `${cumulativeCount}+`
                      : cumulativeCount;
                  to =
                    shopifyProducts.length == shopifyPaging.pageSize
                      ? to
                      : cumulativeCount;
                  // When search filter is applied
                  return `${
                    currentCount > 0 ? from : 0
                  }–${to} of ${displayCount} (filtered)`;
                }
              }}
              ActionsComponent={(subProps) => (
                <PaginationActions
                  {...subProps}
                  hasNextPage={pageInfo?.hasNextPage === "True"}
                  hasPreviousPage={pageInfo?.hasPreviousPage === "True"}
                />
              )}
              sx={{
                border: "none",
                "& .MuiTablePagination-toolbar": {
                  paddingLeft: 0,
                  minHeight: "auto",
                },
              }}
            />
          </Box>
        </Box>
      </Card>
    </>
  );
  function PaginationActions({
    page,
    onPageChange,
    hasNextPage,
    hasPreviousPage,
  }: {
    page: number;
    rowsPerPage: number;
    onPageChange: (
      event: React.MouseEvent<HTMLButtonElement> | null,
      newPage: number,
    ) => void;
    hasNextPage?: boolean;
    hasPreviousPage?: boolean;
  }) {
    const handleBack = (event: React.MouseEvent<HTMLButtonElement>) => {
      onPageChange(event, page - 1);
    };

    const handleNext = (event: React.MouseEvent<HTMLButtonElement>) => {
      onPageChange(event, page + 1);
    };

    return (
      <div style={{ flexShrink: 0, marginLeft: "8px" }}>
        <IconButton
          onClick={handleBack}
          disabled={!hasPreviousPage || page === 0}
          aria-label="Previous Page"
        >
          <KeyboardArrowLeft />
        </IconButton>
        <IconButton
          onClick={handleNext}
          disabled={!hasNextPage}
          aria-label="Next Page"
        >
          <KeyboardArrowRight />
        </IconButton>
      </div>
    );
  }
};

export default ShopifyProductGrid;
